import type {
    LookupCreditCardFeeResponse,
    LookupCreditCardFeeSchemaResponse,
} from '@getstep/proto/acquiring'
import type { Amount } from '@getstep/proto/common'
import type { TransferStatus } from '@getstep/proto/transfer'
import { addAmounts } from '@getstep/sdk/dist/http/models/Amount'
import { splitNames } from '@getstep/sdk/dist/util/Names'
import { CustomModalId } from '@graphql/hooks/modal'
import type { Personalizations } from '@lib/hooks/usePersonalizations'
import { PaymentIntentStage } from '@lib/store/p2p/PaymentIntentStage'
import { PaymentError } from '@lib/store/p2p/PaymentIntentStore'
import {
    makePersistentObservable,
    PersistentStore,
} from '@lib/store/persistence/PersistentStore'
import type { PublicProfileState } from '@lib/store/PublicProfilePageStore'
import type { SdkClientStore } from '@lib/store/SdkClientStore'
import { modalStore } from '@lib/store/ui/modal'
import { formatAmount, formatAsAmount, formatPercent } from '@lib/utils/number'
import { CardTypeForDisplay, formatCard } from '@lib/utils/payment'
import type {
    PaymentAddress,
    PaymentContactInfo,
    TokenizedCard,
} from '@lib/utils/payment'
import { TEXT } from '@lib/utils/text'
import { captureException } from '@sentry/react'
import { LINK } from '@shared/util/link'
import type {
    PaymentRequestPaymentMethodEvent,
    PaymentRequestWallet,
    Stripe,
    StripeCardNumberElement,
} from '@stripe/stripe-js'
import { computed, observable } from 'mobx'
import type { NextRouter } from 'next/router'

type Fees = LookupCreditCardFeeSchemaResponse & LookupCreditCardFeeResponse

/**
 * Store for working with send/request money landing pages.
 */
export class ProfilePaymentStore extends PersistentStore {
    static readonly className = 'ProfilePaymentStore'
    static readonly persistentFields = [
        'addressInfo',
        'amount',
        'attempts',
        'authorizationUrl',
        'card',
        'contactInfo',
        'fees',
        'paymentMethodId',
        'walletName',
    ]

    static readonly ttl = 60 // minutes

    /**
     * Number of times the user has attempted to pay.
     */
    attempts = 0

    /**
     * Indicates the store is loading something
     */
    loading = false

    /**
     * Transaction fee info.
     */
    fees!: Fees

    /**
     * Transfer amount, not including fees.
     */
    amount: Amount = {
        currency: 'USD',
        value: '',
    }

    /**
     * Payer's contact info.
     */
    contactInfo: PaymentContactInfo = {
        fullName: '',
        email: '',
        phone: '',
    }

    /**
     * Parts of payer's address.
     */
    addressInfo: PaymentAddress = {
        zipPostalCode: '',
    }

    /**
     * Card info fit for display.
     */
    card: TokenizedCard = {
        last4: '',
        token: '',
        type: CardTypeForDisplay.Other,
    }

    stripe?: { sdk: Stripe; cardNumberElement: StripeCardNumberElement }

    /**
     * The payment wallet used to make the payment
     */
    walletName?: PaymentRequestWallet

    /**
     * The payment method id used to make the payment
     */
    paymentMethodId?: string

    /**
     * The URL for 3DS authorization if required for the transfer
     */
    authorizationUrl = ''

    /**
     * The current username of the profile being loaded
     */
    username = ''

    constructor(
        readonly sdk: SdkClientStore,
        readonly profile: PublicProfileState,
    ) {
        super()

        this.username = profile?.username ?? ''

        makePersistentObservable(this, {
            authorizationUrl: observable,
            card: observable.deep,
            formattedCard: computed,
        })
    }

    get showWalletOptions(): boolean {
        return this.amountInCents > 0 // amountInCents has to be calculated for stripe payment request
    }

    /**
     * Formats amount and currency for display.
     */
    get amountFormatted() {
        return formatAsAmount(this.amount?.value, this.amount?.currency)
    }

    /**
     * Total amount that will be charged on the card, including fees.
     */
    get totalAmount(): Amount {
        if (!this.fee) {
            return this.amount
        }

        return addAmounts(this.fee, this.amount)
    }

    /**
     * Formats total amount and currency for display.
     */
    get totalAmountFormatted() {
        return formatAsAmount(
            this.totalAmount?.value,
            this.totalAmount?.currency,
        )
    }

    /**
     * Formats amount in cents
     */
    get amountInCents() {
        return this.amount?.value ? Number(this.amount?.value) * 100 : 0
    }

    /**
     * Returns true if this payment method has a transaction fee.
     */
    get fee(): Amount | undefined {
        if (this.card.type !== CardTypeForDisplay.Credit) return

        return this.fees?.fee
    }

    /**
     * Formats fee in cents
     */
    get feeInCents() {
        return this.fee?.value ? Number(this.fee?.value) * 100 : 0
    }

    /**
     * Returns the current fee info fit for display.
     */
    get formattedFee() {
        if (!this.fee) return
        return formatAmount(this.fee)
    }

    /**
     * Returns the generic fee info fit for display.
     */
    get formattedFeeSchema() {
        if (!this.fees) return

        const {
            fee,
            schema: { maxFee, minFee, feePercent },
        } = this.fees

        const zero = formatAmount({ ...fee, value: '0' })

        const formatValue = (value?: Amount) => {
            if (!value) return ''
            const formatted = formatAmount(value)
            if (formatted === zero) return ''
            return formatted
        }

        return {
            maxFee: formatValue(maxFee),
            minFee: formatValue(minFee),
            percent: formatPercent(feePercent),
        }
    }

    /**
     * Returns the card info fit for display.
     */
    get formattedCard() {
        return formatCard(this.card, this.walletName)
    }

    /**
     * Something to be put into PersonalizationProvider
     */
    get personalizations(): Personalizations {
        const {
            formattedCard,
            contactInfo,
            formattedFee,
            formattedFeeSchema,
            profile,
            amountFormatted,
            totalAmountFormatted,
        } = this

        return {
            ...formattedFeeSchema,
            amountFormatted,
            cardholderName: contactInfo.fullName ?? '',
            creditCardFeeFormatted: formattedFee ?? '',
            email: contactInfo.email,
            firstName: profile?.displayName
                ? splitNames(profile?.displayName).firstName
                : '',
            fullName: profile?.displayName,
            maskedCardNumber: formattedCard,
            name: profile?.displayName,
            pictureUrl: profile?.pictureUrl,
            totalAmountFormatted,
        }
    }

    /**
     * Sets any value on the store by merging new values with the existing values
     */
    setValues = (values: Partial<ProfilePaymentStore>) => {
        Object.assign(this, values)
    }

    /**
     * Sets the stripe elements and sdk
     */
    setStripeAndElements = (
        cardNumberElement: StripeCardNumberElement,
        stripe: Stripe,
    ) => {
        this.stripe = { sdk: stripe, cardNumberElement }
    }

    /**
     * Moves the store to the Input stage.
     */
    start = async (router: NextRouter) => {
        const api = await this.sdk.createPublicApi()
        this.fees = await api.getCreditCardFees({
            ...this.amount,
        })

        await this.moveTo(PaymentIntentStage.Input, router)
    }

    /**
     * Saves information related to Apple or Google Pay
     */
    setPaymentMethod = async (
        router: NextRouter,
        ev: PaymentRequestPaymentMethodEvent,
    ) => {
        this.setValues({
            card: {
                token: '',
                last4: ev.paymentMethod.card?.last4 ?? '',
                type:
                    (ev.paymentMethod.card?.funding as CardTypeForDisplay) ??
                    '',
            },
            paymentMethodId: ev.paymentMethod.id,
            walletName: ev.walletName as PaymentRequestWallet,
            contactInfo: {
                fullName: ev.payerName ?? '',
                email: ev.payerEmail ?? '',
                phone: ev.payerPhone ?? '',
            },
            stripe: undefined, // clear out the stripe elements instance since we got the token from Apple/Google Pay
        })

        await this.moveTo(PaymentIntentStage.Confirmation, router)
    }

    confirm = async (router: NextRouter) => {
        const {
            amount,
            addressInfo: { zipPostalCode },
            contactInfo: { email, fullName, phone },
            stripe,
            card,
        } = this

        // if for some reason the amount is empty, go back to input one
        if (amount?.value === undefined) {
            await this.moveTo(undefined, router)
            return
        }

        // if no stripe, but we have a token, user already has a card and is navigating back and forth
        if (!stripe && card.token) {
            await this.moveTo(PaymentIntentStage.Confirmation, router)
            return
        }

        if (!stripe) {
            throw new PaymentError()
        }

        const { cardNumberElement, sdk } = stripe

        const [{ paymentMethod }, { token }] = await Promise.all([
            sdk.createPaymentMethod({
                type: 'card',
                card: cardNumberElement,
                billing_details: {
                    name: fullName,
                    email: email,
                    phone: phone,
                    address: {
                        postal_code: zipPostalCode,
                    },
                },
            }),
            sdk.createToken(cardNumberElement, {
                name: fullName,
                address_country: 'US',
                address_zip: zipPostalCode,
                currency: 'usd',
            }),
        ])

        if (!token?.card || !paymentMethod?.card) {
            throw new PaymentError(TEXT.enterCardInfo)
        }

        const {
            card: { funding, last4 },
        } = paymentMethod

        const type = [
            CardTypeForDisplay.Credit,
            CardTypeForDisplay.Debit,
            CardTypeForDisplay.Prepaid,
        ].includes(funding as CardTypeForDisplay)
            ? (funding as CardTypeForDisplay)
            : CardTypeForDisplay.Other

        if (type === CardTypeForDisplay.Other) {
            throw new PaymentError(TEXT.onlyCreditAndDebit)
        }

        this.setValues({
            card: {
                token: token.id,
                last4,
                type,
            },
            paymentMethodId: undefined,
            walletName: undefined,
            stripe: undefined,
        })

        // clear stripe elements after we make a payment method, force re-initilization next time we try to use (back button)
        this.stripe = undefined

        await this.moveTo(PaymentIntentStage.Confirmation, router)
    }

    /**
     * Finishes payment flow.
     * Moves the store to the Payment stage.
     */

    submit = async (
        router: NextRouter,
        personalizations: Personalizations,
    ): Promise<boolean> => {
        this.authorizationUrl = ''

        const {
            contactInfo: { fullName, email },
            card,
            totalAmount,
            fee,
        } = this

        // if for some reason the amount is empty, go back to input one
        if (this.amount?.value === undefined) {
            await this.moveTo(undefined, router)
            return false
        }

        // reassign to new variable so we can remove from store before we attempt to use
        const token = card.token
        const paymentMethod = this.paymentMethodId

        // always kill card token and paymentMethod since they are one time use
        this.setValues({
            card: { ...this.card, token: '' },
            paymentMethodId: '',
        })

        if (!token && !paymentMethod) {
            this.moveTo(PaymentIntentStage.Input, router)
            return false
        }

        await this.moveTo(PaymentIntentStage.Payment, router)

        const api = await this.sdk.createPublicApi()

        try {
            let status: TransferStatus | undefined
            if (paymentMethod) {
                status = await api.sendMoneytoUsername(
                    totalAmount,
                    fee,
                    {
                        stripe: {
                            paymentMethod,
                        },
                    },
                    this.username,
                    email,
                    fullName,
                )
            } else {
                status = await api.sendMoneytoUsername(
                    totalAmount,
                    fee,
                    {
                        stripe: {
                            token,
                        },
                    },
                    this.username,
                    email,
                    fullName,
                )
            }

            if (status.success) {
                await this.moveTo(PaymentIntentStage.Success, router)
                return true
            } else if (status.pending?.requires3DS) {
                this.authorizationUrl = decodeURIComponent(
                    status.pending.requires3DS.authorizationUrl,
                )
                return true
            } else if (status.pending) {
                await this.moveTo(PaymentIntentStage.Success, router)
                return true
            }

            this.attempts++

            // if we have already tried 3 times, just go to the fail page
            if (this.attempts === 3) {
                await this.moveTo(PaymentIntentStage.Fail, router)
                return false
            }

            // show the modal to ask the user if they want to try again
            const shouldRetry = await modalStore.promptById(
                CustomModalId.PaymentFailed,
                {
                    ...personalizations,
                    errorMessage: status.failure?.error
                        ? (TEXT.httpErrors as any)[
                              status.failure.error.toString()
                          ]
                        : '',
                },
            )

            // user selected "try again" in the modal
            if (shouldRetry) {
                await this.moveTo(PaymentIntentStage.Input, router)
                return false
            }

            // user selected "cancel" in the modal and doesn't want to try again
            await this.moveTo(PaymentIntentStage.Fail, router)
            return false
        } catch (error) {
            captureException(error)

            await this.moveTo(PaymentIntentStage.Fail, router)
            return false
        }
    }

    async moveTo(stage: PaymentIntentStage | undefined, router: NextRouter) {
        const link = LINK.profilePay(this.username, stage).serialize()

        if (
            [
                undefined,
                PaymentIntentStage.Input,
                PaymentIntentStage.Confirmation,
            ].includes(stage)
        ) {
            router.push(link)
            return
        }

        router.replace(link)
    }
}
